import React, { useState } from "react";
import axios from "axios";
import { ArrowRight, Instagram, Mail } from "lucide-react";
import { INSTAGRAM_URL, CONTACT_EMAIL } from "../assets";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact({ t }) {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [state, setState] = useState({ loading: false, ok: null, err: null });

  const submit = async (e) => {
    e.preventDefault();
    setState({ loading: true, ok: null, err: null });
    try {
      await axios.post(`${API}/contact`, form);
      setState({ loading: false, ok: t.contact.success, err: null });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setState({ loading: false, ok: null, err: t.contact.error });
    }
  };

  const fieldClass =
    "w-full bg-transparent border-b border-line focus:border-leaf rounded-none px-0 py-4 text-bone placeholder:text-ash outline-none transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-ink border-t border-line py-24 md:py-36 overflow-hidden">
      {/* Huge background type */}
      <p className="absolute inset-x-0 top-1/2 -translate-y-1/2 font-display text-[28vw] md:text-[18vw] leading-none tracking-tighter text-bone/[0.035] uppercase text-center select-none pointer-events-none">
        CONTACT
      </p>

      <div className="relative px-6 md:px-12 lg:px-20 grid md:grid-cols-12 gap-12 md:gap-16">
        <div className="md:col-span-5">
          <div className="flex items-center gap-4 mb-6">
            <span className="block h-px w-10 bg-leaf"></span>
            <p className="text-[11px] tracking-[0.4em] uppercase text-ash">{t.contact.eyebrow}</p>
          </div>
          <h2
            data-testid="contact-title"
            className="font-serif font-light text-bone text-4xl sm:text-5xl md:text-6xl leading-[1.02] tracking-tight"
          >
            {t.contact.title}
            <br />
            <span className="italic">{t.contact.title2}</span>
          </h2>
          <p className="mt-8 text-ash text-base leading-relaxed max-w-md">{t.contact.sub}</p>

          <div className="mt-12 space-y-5">
            <p className="text-[10px] tracking-[0.4em] uppercase text-ash">{t.contact.direct}</p>
            <a
              data-testid="contact-email-link"
              href={`mailto:${CONTACT_EMAIL}`}
              className="link-underline inline-flex items-center gap-3 text-bone hover:text-leaf transition-colors"
            >
              <Mail size={16} strokeWidth={1.5} />
              <span className="text-lg md:text-xl">{CONTACT_EMAIL}</span>
            </a>
            <div>
              <a
                data-testid="contact-instagram-link"
                href={INSTAGRAM_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="link-underline inline-flex items-center gap-3 text-bone hover:text-leaf transition-colors"
              >
                <Instagram size={16} strokeWidth={1.5} />
                <span className="text-lg md:text-xl">@nono.lcv</span>
              </a>
            </div>
          </div>
        </div>

        <form
          data-testid="contact-form"
          onSubmit={submit}
          className="md:col-span-6 md:col-start-7 space-y-2"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
            <div>
              <label className="text-[10px] tracking-[0.4em] uppercase text-ash">{t.contact.name}</label>
              <input
                data-testid="contact-name"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                className={fieldClass}
              />
            </div>
            <div>
              <label className="text-[10px] tracking-[0.4em] uppercase text-ash">{t.contact.email}</label>
              <input
                data-testid="contact-email"
                required
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                className={fieldClass}
              />
            </div>
          </div>

          <div className="pt-6">
            <label className="text-[10px] tracking-[0.4em] uppercase text-ash">{t.contact.subject}</label>
            <input
              data-testid="contact-subject"
              value={form.subject}
              onChange={(e) => setForm({ ...form, subject: e.target.value })}
              className={fieldClass}
            />
          </div>

          <div className="pt-6">
            <label className="text-[10px] tracking-[0.4em] uppercase text-ash">{t.contact.message}</label>
            <textarea
              data-testid="contact-message"
              required
              rows={5}
              value={form.message}
              onChange={(e) => setForm({ ...form, message: e.target.value })}
              className={`${fieldClass} resize-none`}
            />
          </div>

          <div className="pt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
            <button
              data-testid="contact-submit"
              type="submit"
              disabled={state.loading}
              className="group inline-flex items-center gap-3 border border-bone/40 hover:border-leaf hover:bg-leaf/10 px-7 py-4 transition-all duration-500 disabled:opacity-50"
            >
              <span className="text-[11px] tracking-[0.32em] uppercase text-bone">
                {state.loading ? t.contact.sending : t.contact.send}
              </span>
              <ArrowRight
                size={16}
                strokeWidth={1.5}
                className="text-bone group-hover:translate-x-1 transition-transform"
              />
            </button>

            {state.ok && (
              <p data-testid="contact-success" className="text-leaf text-sm">
                {state.ok}
              </p>
            )}
            {state.err && (
              <p data-testid="contact-error" className="text-red-400/90 text-sm">
                {state.err}
              </p>
            )}
          </div>
        </form>
      </div>
    </section>
  );
}
